import plusIcon from "@loot-os/icons/icons/plus";
import type { ReactElement } from "react";

import { Card } from "../core/Card.js";
import { CardLink } from "../core/CardLink.js";
import { Icon } from "../core/Icon.js";
import { cn } from "../foundations/cn.js";
import { OptionalImage } from "../foundations/OptionalImage.js";

/**
 * Where the card goes: an `href`, or the app's own link element (which wins over the
 * LinkProvider's). One of them is required: the card is only ever a way into the builder.
 */
export type CreateBoxCardDestination =
  | { href: string; render?: ReactElement }
  | { href?: string; render: ReactElement };

export type CreateBoxCardProps = CreateBoxCardDestination & {
  /** What the card does, and its link's name: "Create". */
  label: string;
  /** A line under the plus: "Pick the cards, then open it or share it". */
  hint?: string;
  /**
   * Box art fanned faintly behind the plus, the store's own packs (the first three are drawn):
   * what the box could look like. Left out, the well holds the plus and the glow alone.
   */
  art?: readonly string[];
  className?: string;
};

/**
 * A fanned pack's place at rest and once the card is hovered or focused, by where it sits: the
 * outer two lean out and spread, the middle one rises. Under reduced motion they stay at rest.
 * Complete class names, so Tailwind reads them.
 */
const FAN = {
  left: "-translate-x-[30%] -rotate-12 motion-safe:group-hover:-translate-x-[40%] motion-safe:group-hover:-rotate-[16deg] motion-safe:group-focus-within:-translate-x-[40%] motion-safe:group-focus-within:-rotate-[16deg]",
  right:
    "translate-x-[30%] rotate-12 motion-safe:group-hover:translate-x-[40%] motion-safe:group-hover:rotate-[16deg] motion-safe:group-focus-within:translate-x-[40%] motion-safe:group-focus-within:rotate-[16deg]",
  middle:
    "-translate-y-[2%] motion-safe:group-hover:-translate-y-[6%] motion-safe:group-focus-within:-translate-y-[6%]",
} as const;

/** Where each of one, two or three packs sits, in drawing order: the middle one last, in front. */
const FAN_PLACES = [[], ["middle"], ["left", "right"], ["left", "right", "middle"]] as const;

/** The accent fill a hovered or focused card's plus and bar take. */
const LIT =
  "group-hover:bg-accent-default group-hover:text-text-on-accent group-focus-within:bg-accent-default group-focus-within:text-text-on-accent";

/**
 * The place for a box not built yet, among the boxes that are: a dashed card a `BoxCard`'s size
 * and shape, the store's packs fanned faintly in its well around a plus over an accent glow, and
 * the action in the bar where a box card shows its price. At rest it is still; on hover or
 * keyboard focus the packs spread, the glow lifts, and the plus and bar take the accent. One link,
 * named by the label.
 */
export function CreateBoxCard({
  label,
  hint,
  art = [],
  href,
  render,
  className,
}: CreateBoxCardProps) {
  const fanned = art.slice(0, 3);
  const places = FAN_PLACES[fanned.length] ?? [];
  return (
    <Card
      radius="2xl"
      fill="none"
      edge="dashed"
      action="link"
      hover="raise"
      className={cn("group relative flex h-full flex-col gap-3 p-3", className)}
    >
      <div className="flex aspect-[216/260] flex-1 flex-col items-center gap-2 overflow-hidden rounded-lg px-4 pb-4 text-center">
        {/* The art, filling the card above the bar: the store's packs fanned faintly around the plus, over the accent glow. */}
        <div className="relative isolate flex w-full flex-1 items-center justify-center">
          {/* The accent glow, dim at rest, lifting on hover or focus. */}
          <span
            aria-hidden
            className="absolute inset-0 -z-20 bg-[radial-gradient(closest-side,color-mix(in_oklab,var(--color-accent-default)_35%,transparent),transparent)] opacity-40 transition-opacity duration-slow ease-standard group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none"
          />
          {/* Faded as one layer, so the front pack covers the ones behind it rather than showing
              their edges through. */}
          <div
            aria-hidden
            className="absolute inset-0 -z-10 opacity-30 transition-opacity duration-slow ease-standard group-hover:opacity-60 group-focus-within:opacity-60 motion-reduce:transition-none"
          >
            {places.map((place, i) => (
              <OptionalImage
                key={place}
                src={fanned[i]}
                alt=""
                className={cn(
                  "absolute top-[17%] left-1/2 -ml-[27%] h-[66%] w-[54%] object-contain transition-[translate,rotate] duration-slow ease-smooth motion-reduce:transition-none",
                  FAN[place],
                )}
              />
            ))}
          </div>
          <span
            aria-hidden
            className={cn(
              "flex size-14 items-center justify-center rounded-pill bg-bg-raised text-text-primary shadow-lg transition-[scale] duration-base ease-smooth motion-safe:group-hover:scale-110 motion-safe:group-focus-within:scale-110 motion-reduce:transition-none",
              LIT,
            )}
          >
            <Icon icon={plusIcon} size={24} />
          </span>
        </div>
        {hint && <span className="font-body text-body-b3 text-text-secondary">{hint}</span>}
      </div>
      {/* Where a box card shows its price: the action, filling with the accent. */}
      <span
        className={cn(
          "mt-auto flex h-control-md items-center justify-center rounded-lg bg-bg-raised font-body text-body-b2-strong text-text-primary",
          LIT,
        )}
      >
        <CardLink href={href} render={render}>
          {label}
        </CardLink>
      </span>
    </Card>
  );
}
